<template>
  <section
    v-if="visibleSlides.length"
    :aria-label="$t('component.marketingcarousel.arialabel')"
    aria-roledescription="carousel"
    class="marketing-carousel mb-200"
  >
    <LcCarousel
      arrows
      auto-play
      :key-extractor="keyExtractor"
      loop
      pagination
      :slides="visibleSlides"
      v-bind="{ fade }"
      @changed="activeIndex = $event"
    >
      <template #slide="{ attributes, index, slide }">
        <!-- click listener is tracking-only; keyboard activation of the link bubbles here too -->
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
        <div
          v-bind="attributes"
          :aria-label="slide.altText"
          class="slide"
          @click="emit('slideClick', slide)"
        >
          <HyperLink v-if="slide.to" class="link" :to="slide.to">
            <NuxtImg v-bind="imageAttributes(slide, index)" />
          </HyperLink>
          <NuxtImg v-else v-bind="imageAttributes(slide, index)" />
        </div>
      </template>
    </LcCarousel>

    <p aria-live="polite" class="announcement">{{ activeSlide?.altText }}</p>
  </section>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'

export type MarketingCarouselSlideProps = {
  altText: string
  imageUrl: string
  to?: RouteLocationRaw
  trackingName: string
}

export type MarketingCarouselProps = {
  slides: MarketingCarouselSlideProps[]
}

type Emits = {
  slideClick: [slide: MarketingCarouselSlideProps]
}

const { slides } = defineProps<MarketingCarouselProps>()

const emit = defineEmits<Emits>()
const isMounted = useMounted()
const mediaIsDesktop = useMediaQuery('screen and (width >= 768px)')
const activeIndex = ref(0)
const failedImageUrls = ref(new Set<string>())

const fade = computed(() => isMounted.value && mediaIsDesktop.value)

const visibleSlides = computed(() =>
  slides.filter(({ imageUrl }) => !failedImageUrls.value.has(imageUrl)),
)
const activeSlide = computed(() => visibleSlides.value[activeIndex.value])

const handleImageError = (imageUrl: string) => {
  failedImageUrls.value.add(imageUrl)
}

const keyExtractor = (slide: MarketingCarouselSlideProps) => slide.imageUrl

const imageAttributes = (slide: MarketingCarouselSlideProps, index: number) => ({
  alt: slide.altText,
  fetchpriority: index === 0 ? ('high' as const) : undefined,
  onError: () => handleImageError(slide.imageUrl),
  preload: index === 0,
  src: slide.imageUrl,
})
</script>

<style lang="scss" scoped>
$snap-width: 440px;

.marketing-carousel {
  --marketing-carousel-banner-height: 11.5rem;

  width: 100%;
  max-width: 47rem;
  margin-inline: auto;

  @media screen and (width >= $snap-width) {
    --marketing-carousel-banner-height: 17.125rem;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-carousel__container) {
    height: var(--marketing-carousel-banner-height);
    border-radius: token('size/radius/l');
    background: linear-gradient(180deg, token('color/blue/900') 0%, token('color/blue/950') 100%);
  }

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-carousel__arrows) {
    display: none;

    @media screen and (width >= $snap-width) {
      display: flex;
    }
  }

  .slide {
    flex: 0 0 100%;
    height: var(--marketing-carousel-banner-height);

    .link {
      display: block;
      height: 100%;
    }

    :deep(img) {
      object-fit: cover;
      object-position: center;
      width: 100%;
      height: 100%;
    }
  }

  .announcement {
    position: absolute;
    overflow: hidden;
    clip-path: inset(50%);
    width: 1px;
    height: 1px;
    white-space: nowrap;
  }
}
</style>
